<template>
  <article
    class="og-home-article inside-article section-content"
    itemscope
    itemtype="https://schema.org/Article"
  >
    <div class="og-article-body" v-html="articleBeforeScreenshots" />
    <OgHomeScreenshots />
    <div class="og-article-body og-article-body--continued" v-html="articleAfterScreenshots" />
  </article>
</template>

<script>
import { buildOgArticleHtml } from '@/content/ogHomeArticleContent.js';
import OgHomeScreenshots from '@/views/partials/OgHomeScreenshots.vue';

const SCREENSHOTS_SPLIT = '<!--OG_HOME_SCREENSHOTS-->';

export default {
  name: 'OgHomeArticle',
  components: { OgHomeScreenshots },
  computed: {
    articleBeforeScreenshots() {
      const full = buildOgArticleHtml();
      const i = full.indexOf(SCREENSHOTS_SPLIT);
      if (i === -1) return full;
      return full.slice(0, i);
    },
    articleAfterScreenshots() {
      const full = buildOgArticleHtml();
      const i = full.indexOf(SCREENSHOTS_SPLIT);
      if (i === -1) return '';
      return full.slice(i + SCREENSHOTS_SPLIT.length);
    },
  },
};
</script>

<style lang="scss" scoped>
/* 与首屏同宽；覆盖全局 .section-content 的 75% */
.og-home-article.inside-article.section-content {
  width: 100% !important;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
  padding: 12px max(16px, env(safe-area-inset-right)) 40px max(16px, env(safe-area-inset-left));
  box-sizing: border-box;
}

.og-home-article {
  line-height: 1.65;
  color: #333333;
  border-top: 1px solid #e5e5e5;
  margin-top: 0;
  background: #ffffff;
}

/* v-html 内元素：使用后代选择器（仍受 scoped 父级限制） */
.og-article-body ::v-deep .og-rich p {
  margin: 0 0 1rem;
  font-size: 1.02rem;
  line-height: 1.7;
  color: #333333;
}

.og-article-body ::v-deep .og-rich p:last-child {
  margin-bottom: 0;
}

/* 大章节 H2：深色底 + 白字，层级最强 */
.og-article-body ::v-deep h2 {
  margin: 2.35rem 0 1rem;
  font-size: clamp(1.45rem, 3.2vw, 1.85rem);
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.02em;
  line-height: 1.25;
  padding: 0.7rem 1rem 0.75rem;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #204f46 0%, #2a6b5c 100%);
  box-shadow: 0 6px 20px rgba(32, 79, 70, 0.28);
}

.og-article-body ::v-deep h2:first-of-type {
  margin-top: 1.25rem;
}

/* 续篇首段 H2：紧接轮播后略收顶距 */
.og-article-body--continued ::v-deep h2:first-of-type {
  margin-top: 0.5rem;
}

/* 小节 H3：明显小于 H2，浅底 + 左边条 */
.og-article-body ::v-deep h3:not(.og-install-timeline__title) {
  margin: 1.4rem 0 0.55rem;
  font-size: clamp(1rem, 2.2vw, 1.14rem);
  font-weight: 700;
  color: #1a3d36;
  line-height: 1.35;
  padding: 0.42rem 0.75rem 0.48rem;
  border-radius: 8px;
  background: #e8f4f0;
  border-left: 4px solid #34d399;
  box-shadow: 0 1px 0 rgba(32, 79, 70, 0.06);
}

.og-article-body ::v-deep a {
  color: #204f46 !important;
  font-weight: 600;
  text-decoration: none !important;
  text-underline-offset: 2px;
}

.og-article-body ::v-deep a:hover {
  color: #34d399 !important;
  text-decoration: underline !important;
  text-decoration-thickness: 1px;
}

.og-article-body ::v-deep code {
  font-size: 0.88em;
  padding: 0.15em 0.45em;
  border-radius: 6px;
  background: #ffffff;
  color: #204f46;
  border: 1px solid #e5e5e5;
}

/* 表格 */
.og-article-body ::v-deep .og-content-table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.25rem 0;
  font-size: 0.95rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.08);
  border: 1px solid #e5e5e5;
}

.og-article-body ::v-deep .og-content-table th,
.og-article-body ::v-deep .og-content-table td {
  padding: 12px 14px;
  text-align: left;
  border-bottom: 1px solid #e5e5e5;
}

.og-article-body ::v-deep .og-content-table thead th {
  background: #204f46;
  color: #ffffff;
  font-weight: 700;
  font-size: 0.82rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.og-article-body ::v-deep .og-content-table tbody tr:nth-child(even) td {
  background: #f8fafc;
}

.og-article-body ::v-deep .og-content-table tbody tr:hover td {
  background: #f1f5f9;
}

.og-article-body ::v-deep .og-content-table tbody td:first-child {
  font-weight: 600;
  color: #333333;
  width: 34%;
}

.og-article-body ::v-deep .og-content-table--compare tbody td:first-child {
  width: 28%;
}

/* 警示 / 提示块 */
.og-article-body ::v-deep .og-callout {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  border-radius: 12px;
  margin: 1rem 0;
  font-size: 0.95rem;
  line-height: 1.55;
}

.og-article-body ::v-deep .og-callout__icon {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-weight: 800;
  font-size: 0.95rem;
}

.og-article-body ::v-deep .og-callout--warning {
  background: #fff8f0;
  border: 1px solid #f5d0a8;
  color: #5c3d12;
}

.og-article-body ::v-deep .og-callout--warning .og-callout__icon {
  background: #fef3e2;
  color: #b45309;
}

.og-article-body ::v-deep .og-callout--important {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #7f1d1d;
}

.og-article-body ::v-deep .og-callout--important .og-callout__icon {
  background: #fee2e2;
  color: #b91c1c;
}

.og-article-body ::v-deep .og-callout--tip {
  background: #ffffff;
  border: 1px solid #e5e5e5;
  color: #ff7a00;
}

.og-article-body ::v-deep .og-callout--tip .og-callout__icon {
  background: #ffffff;
  color: #ff7a00;
  border: 1px solid #e5e5e5;
}

/* 安装时间线 */
.og-article-body ::v-deep .og-install-timeline {
  list-style: none;
  margin: 1.5rem 0;
  padding: 0;
  position: relative;
}

.og-article-body ::v-deep .og-install-timeline__item {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 0 16px;
  margin-bottom: 0;
  padding-bottom: 1.5rem;
  position: relative;
}

.og-article-body ::v-deep .og-install-timeline__item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 23px;
  top: 48px;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, #204f46, rgba(32, 79, 70, 0.15));
  border-radius: 999px;
}

.og-article-body ::v-deep .og-install-timeline__marker {
  width: 46px;
  height: 46px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 1.1rem;
  color: #fff;
  background: #204f46;
  box-shadow: 0 4px 14px rgba(32, 79, 70, 0.25);
  position: relative;
  z-index: 1;
}

.og-article-body ::v-deep .og-install-timeline__body {
  background: #f8fafc;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
  padding: 16px 18px;
  margin-bottom: 0;
}

.og-article-body ::v-deep .og-install-timeline__title {
  margin: 0 0 0.5rem;
  font-size: 1.05rem;
  font-weight: 800;
  color: #204f46;
  padding: 0;
  border: none;
  border-left: none;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.og-article-body ::v-deep .og-install-timeline__body > p {
  margin: 0 0 0.5rem;
  font-size: 0.96rem;
}

/* 勾选列表 */
.og-article-body ::v-deep .og-icon-list {
  list-style: none;
  margin: 1rem 0 1.25rem;
  padding: 0;
}

.og-article-body ::v-deep .og-icon-list--check li {
  position: relative;
  padding-left: 2rem;
  margin-bottom: 0.65rem;
  font-size: 0.98rem;
  line-height: 1.55;
  color: #333333;
}

.og-article-body ::v-deep .og-icon-list--check li::before {
  content: '✓';
  position: absolute;
  left: 0;
  top: 0.05rem;
  width: 1.35rem;
  height: 1.35rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 800;
  color: #fff;
  background: #204f46;
  border: none;
  border-radius: 6px;
}

@media (max-width: 520px) {
  .og-article-body ::v-deep .og-install-timeline__item {
    grid-template-columns: 40px 1fr;
    gap: 0 10px;
  }

  .og-article-body ::v-deep .og-install-timeline__marker {
    width: 38px;
    height: 38px;
    font-size: 0.95rem;
  }

  .og-article-body ::v-deep .og-install-timeline__item:not(:last-child)::before {
    left: 18px;
    top: 42px;
  }
}
</style>
